import { RE2JS } from 're2js';
import type { IntlShape } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { ErrorKeySet, type Validator } from '../../../types/field-validation';
import type { FormFieldValidation } from '../../../types/form/form-fields';
import type { TextValue } from '../../../types/form/form-fields/field-values';
import { getFailedRequiredValidationError, getFailedValidationError } from '../utils';
import { messages } from './messages';

const wordCounter = (text: string): number => (text.trim().match(/\S+/g) || []).length;

const shouldValidateWordCount = (wordCount: number | undefined): wordCount is number =>
	Boolean(wordCount);

export const getTextValidationErrors = (
	validation: FormFieldValidation,
	fieldLabel: string,
	intl?: IntlShape,
	textValue?: TextValue,
): ReturnType<Validator> => {
	if (Object.keys(validation).length === 0) {
		return [];
	}

	if (!textValue) {
		if (validation.required) {
			return [getFailedRequiredValidationError(fieldLabel, intl)];
		}
		return [];
	}

	const { text } = textValue;
	const trimmedText = text.trim();
	const wordCount =
		validation.minimumWords || validation.maximumWords ? wordCounter(trimmedText) : undefined;

	if (trimmedText.length === 0) {
		if (validation.required) {
			return [getFailedRequiredValidationError(fieldLabel, intl)];
		}
		return [];
	}

	if (trimmedText.length > 0) {
		// the char count in the trimmed text does not match the char count in the validation rule
		if (
			validation.minimumCharacters &&
			validation.minimumCharacters === validation.maximumCharacters &&
			trimmedText.length !== validation.minimumCharacters
		) {
			return [
				getFailedValidationError(
					ErrorKeySet.INVALID,
					intl?.formatMessage(messages.exactCharacters, { minimum: validation.minimumCharacters }),
				),
			];
		}

		// the word count in the trimmed text does not match the word count in the validation rule
		if (
			validation.minimumWords &&
			validation.minimumWords === validation.maximumWords &&
			wordCount !== validation.minimumWords
		) {
			return [
				getFailedValidationError(
					ErrorKeySet.INVALID,
					intl?.formatMessage(messages.exactWords, { minimum: validation.minimumWords }),
				),
			];
		}

		// the char count in the trimmed text is outside the [min,max] range in the validation rule
		if (
			validation.minimumCharacters &&
			validation.maximumCharacters &&
			(trimmedText.length < validation.minimumCharacters ||
				trimmedText.length > validation.maximumCharacters)
		) {
			return [
				getFailedValidationError(
					ErrorKeySet.INVALID,
					intl?.formatMessage(messages.betweenCharacters, {
						minimum: validation.minimumCharacters,
						maximum: validation.maximumCharacters,
					}),
				),
			];
		}

		// the word count in the trimmed text is outside the [min,max] range in the validation rule
		if (
			validation.minimumWords &&
			validation.maximumWords &&
			shouldValidateWordCount(wordCount) &&
			(wordCount < validation.minimumWords || wordCount > validation.maximumWords)
		) {
			return [
				getFailedValidationError(
					ErrorKeySet.INVALID,
					intl?.formatMessage(messages.betweenWords, {
						minimum: validation.minimumWords,
						maximum: validation.maximumWords,
					}),
				),
			];
		}

		// the char count in the trimmed text is below the min value in the validation rule
		if (validation.minimumCharacters && trimmedText.length < validation.minimumCharacters) {
			return [
				getFailedValidationError(
					ErrorKeySet.INVALID,
					intl?.formatMessage(messages.minimumCharacters, {
						minimum: validation.minimumCharacters - 1,
					}),
				),
			];
		} // Note: threshold is modified here to fit the validation error messages wording see (PF-1683)

		// the word count in the trimmed text is below the min value in the validation rule
		if (
			validation.minimumWords &&
			shouldValidateWordCount(wordCount) &&
			wordCount < validation.minimumWords
		) {
			return [
				getFailedValidationError(
					ErrorKeySet.INVALID,
					intl?.formatMessage(messages.minimumWords, { minimum: validation.minimumWords - 1 }),
				),
			];
		} // Note: threshold is modified here to fit the validation error messages wording see (PF-1683)

		// the char count in the trimmed text is above the max value in the validation rule
		if (validation.maximumCharacters && trimmedText.length > validation.maximumCharacters) {
			return [
				getFailedValidationError(
					ErrorKeySet.INVALID,
					intl?.formatMessage(messages.maximumCharacters, {
						maximum: validation.maximumCharacters + 1,
					}),
				),
			];
		} // Note: threshold is modified here to fit the validation error messages wording see (PF-1683)

		// the word count in the trimmed text is above the max value in the validation rule
		if (
			validation.maximumWords &&
			shouldValidateWordCount(wordCount) &&
			wordCount > validation.maximumWords
		) {
			return [
				getFailedValidationError(
					ErrorKeySet.INVALID,
					intl?.formatMessage(messages.maximumWords, { maximum: validation.maximumWords + 1 }),
				),
			];
		} // Note: threshold is modified here to fit the validation error messages wording see (PF-1683)

		// a custom regex pattern was provided
		if (validation.regexValidation && validation.regexValidation.pattern) {
			try {
				let isMatch = false;

				try {
					const patternString =
						validation.regexValidation.pattern instanceof RegExp
							? validation.regexValidation.pattern.source
							: validation.regexValidation.pattern;
					isMatch = RE2JS.compile(patternString).matcher(text).find();
				} catch {
					if (fg('proforma_enforce_regex_validation')) {
						return [
							getFailedValidationError(
								ErrorKeySet.INVALID_REGEX,
								intl?.formatMessage(messages.regexUnsupportedMessage),
							),
						];
					}
					isMatch = text.match(validation.regexValidation.pattern) !== null;
				}

				if (!isMatch) {
					if (validation.regexValidation.mismatchMessage) {
						return [
							getFailedValidationError(
								ErrorKeySet.INVALID,
								intl?.formatMessage(messages.regexCustomMessage, {
									message: validation.regexValidation.mismatchMessage,
								}),
							),
						];
					}

					return [
						getFailedValidationError(
							ErrorKeySet.INVALID,
							intl?.formatMessage(messages.regexDefaultMessage),
						),
					];
				}
			} catch {
				return [
					getFailedValidationError(
						ErrorKeySet.INVALID,
						intl?.formatMessage(messages.regexInvalidMessage),
					),
				];
			}
		}
	}

	return [];
};
